import * as React from 'react'
import i18next from 'i18next'
import {
  CalendarDaysIcon,
  Squares2X2Icon,
  Bars3Icon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline'
import { AnchoredMenu } from '../AnchoredMenu'
import { GalleryLayoutMode } from '../JustifiedGallery'

export interface GalleryOptionsMenuProps {
  isOpen: boolean
  onClose: () => void
  groupByDate: boolean
  onGroupByDateChange: (value: boolean) => void
  layoutMode: GalleryLayoutMode
  onLayoutModeChange: (value: GalleryLayoutMode) => void
}

const OptionCard: React.FC<{
  selected: boolean
  onClick: () => void
  icon: React.ComponentType<{ className?: string }>
  label: string
}> = ({ selected, onClick, icon: Icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`flex flex-col items-center gap-2 rounded-lg border p-4 transition-colors ${
      selected
        ? 'border-indigo-600 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600'
        : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
    }`}
  >
    <Icon className="h-7 w-7" />
    <span className="text-sm font-medium">{label}</span>
  </button>
)

export const GalleryOptionsMenu: React.FC<GalleryOptionsMenuProps> = ({
  isOpen,
  onClose,
  groupByDate,
  onGroupByDateChange,
  layoutMode,
  onLayoutModeChange,
}) => (
  <AnchoredMenu isOpen={isOpen} onClose={onClose} title={i18next.t('galleryOptions.title')}>
    <div>
      <p className="mb-2 text-sm font-medium text-gray-700">
        {i18next.t('galleryOptions.layout')}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <OptionCard
          selected={layoutMode === 'justified'}
          onClick={() => onLayoutModeChange('justified')}
          icon={Bars3Icon}
          label={i18next.t('galleryOptions.layoutJustified')}
        />
        <OptionCard
          selected={layoutMode === 'masonry'}
          onClick={() => onLayoutModeChange('masonry')}
          icon={ViewColumnsIcon}
          label={i18next.t('galleryOptions.layoutMasonry')}
        />
      </div>
    </div>

    <div>
      <p className="mb-2 text-sm font-medium text-gray-700">
        {i18next.t('galleryOptions.grouping')}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <OptionCard
          selected={groupByDate}
          onClick={() => onGroupByDateChange(true)}
          icon={CalendarDaysIcon}
          label={i18next.t('galleryOptions.byDate')}
        />
        <OptionCard
          selected={!groupByDate}
          onClick={() => onGroupByDateChange(false)}
          icon={Squares2X2Icon}
          label={i18next.t('galleryOptions.continuous')}
        />
      </div>
    </div>
  </AnchoredMenu>
)
